import TextField from '@mui/material/TextField';
import Box from '@mui/material/Box';
import { ReactNode, useState, useEffect } from "react";
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate } from "react-router-dom";
import ErrorAlert from '../common/ErrorAlert';
import { authService } from '../../services/authService';
import { FormControl, FormHelperText, IconButton, InputAdornment, InputLabel, OutlinedInput } from '@mui/material';
import React from 'react';
import TmFullLoadingButton from '../common/buttons/TmFullLoadingButton';
import TmClosedEye from '../../icons/TmClosedEye';
import TmEye from '../../icons/TmEye';
import { ROUTES } from '../../constants/routes';
import TmEsia from '../../icons/TmEsia';
import { featureTogglesService } from '../../services/featureTogglesService';
import Backdrop from '@mui/material/Backdrop';
import Spinner from '../common/Spinner';
import { getApiErrorMessage } from '../../helpers/errorUtils';

export const Login = () => {
    const [loginLoading, setLoginLoading] = useState(false);
    const [esiaLoading, setEsiaLoading] = useState(false);
    const [esiaCodeLoading, setEsiaCodeLoading] = useState(false);
    const { register, handleSubmit, formState: { errors }, } = useForm({
        mode: 'onChange',
    });
    const [error, setError] = useState('');

    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        console.log(window.location.search);
        const params = new URLSearchParams(window.location.search);
        const code = params.get('code');
        if (code) {
            setEsiaCodeLoading(true);
            authService.esiaLogin(code)
                .then(t => {
                    if (t.success) {
                        const searchParams = new URLSearchParams(location.search);
                        const returnUrl = searchParams.get('returnUrl');
                        // Redirect to home page by default, or to returnUrl if provided
                        navigate(returnUrl || ROUTES.home);
                    } else {
                        showAlert(t.message);
                    }
                })
                .catch(e => {
                    const error = (e as any).response?.data?.message ?? (e as any).message;
                    showAlert(error);
                })
                .finally(() => setEsiaCodeLoading(false));
        }
    }, [location.search, navigate]);

    const onSubmit = (data: any) => {
        setLoginLoading(true);
        authService.login(data.email, data.password)
            .then(t => {
                if (t.success) {
                    const searchParams = new URLSearchParams(location.search);
                    const returnUrl = searchParams.get('returnUrl');
                    // Redirect to home page by default, or to returnUrl if provided
                    navigate(returnUrl || ROUTES.home);
                } else {
                    console.log(t);
                    showAlert(t.message);
                }
            })
            .catch(e => {
                showAlert(getApiErrorMessage(e, 'Не удалось подключиться к серверу'));
            })
            .finally(() => setLoginLoading(false));
    };

    const showAlert = (errorMessage: string = 'Произошла ошибка') => {
        setError(errorMessage);
    }

    const [showPassword, setShowPassword] = React.useState(false);

    const handleClickShowPassword = () => setShowPassword((show) => !show);

    const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
    };


    const handleEsiaLogin = async () => {
        setEsiaLoading(true);
        try {
            const response = await authService.getEsiaLoginUrl();
            if (response.data.success && response.data.data) {
                window.location.href = response.data.data;
                // window.open(response.data.data, '_blank', 'noopener,noreferrer');
            } else {
                showAlert(response.data.message || 'Ошибка получения ссылки для входа через Госуслуги');
            }
        } catch (e) {
            const error = (e as any).response?.data?.message ?? (e as any).message;
            showAlert(error);
        } finally {
            setEsiaLoading(false);
        }
    };

    return (
        <div>
            <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ mt: 1 }}>
                <TextField
                    fullWidth
                    label="Электронная почта"
                    type="email"
                    margin="normal"
                    {...register('email', {
                        required: 'Электронная почта не заполнена',
                        minLength: {
                            value: 6,
                            message: 'Электронная почта должна быть больше 6ти символов',
                        },
                        pattern: {
                            value: /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                            message: 'Невалидная электронная почта',
                        },
                    })}
                    error={Boolean(errors.email)}
                    helperText={errors.email?.message as ReactNode}
                />

                <FormControl fullWidth>
                    <InputLabel htmlFor="outlined-adornment-password">
                        {errors.password ? <Box color="#d32f2f">Пароль</Box> : <Box>Пароль</Box>}
                    </InputLabel>
                    <OutlinedInput
                        fullWidth
                        id="outlined-adornment-password"
                        type={showPassword ? 'text' : 'password'}
                        autoComplete="current-password"
                        {...register('password', {
                            required: 'Пароль не заполнен',
                            minLength: {
                                value: 6,
                                message: 'Пароль должен быть больше 6ти символов',
                            },
                        })}
                        endAdornment={
                            <InputAdornment position="end">
                                <IconButton
                                    aria-label="toggle password visibility"
                                    onClick={handleClickShowPassword}
                                    onMouseDown={handleMouseDownPassword}
                                    edge="end"
                                >
                                    {showPassword ? <TmEye fill="none" stroke="#a4a5b3" /> : <TmClosedEye fill="none" stroke="#a4a5b3" />}
                                </IconButton>
                            </InputAdornment>
                        }
                        label="Пароль"
                        error={Boolean(errors.password)}
                    />
                    {!!errors.password && (
                        <FormHelperText error id="password-error">
                            Пароль не заполнен
                        </FormHelperText>
                    )}
                </FormControl>

                <TmFullLoadingButton
                    type="submit"
                    sx={{ mt: 3 }}
                    loading={loginLoading}
                    loadingPosition="center"
                    width="100%"
                >
                    Войти
                </TmFullLoadingButton>
                {featureTogglesService.isEsiaIntegrationEnabled() && (
                    <TmFullLoadingButton
                        type="button"
                        loading={esiaLoading}
                        loadingPosition="start"
                        width="100%"
                        onClick={handleEsiaLogin}
                        disabled={esiaLoading}
                        sx={{ mt: 2, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}
                        startIcon={<TmEsia />}
                    >
                        Войти через Госуслуги
                    </TmFullLoadingButton>
                )}
            </Box>
            {error && (
                <ErrorAlert
                    message={error}
                    onClose={() => setError('')}
                />
            )}
            <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.modal + 1 }} open={esiaCodeLoading}>
                <Spinner />
            </Backdrop>
        </div>
    );
}
